<template>
  <div class="track-map">
    <div ref="mapRoot" class="map-container" :class="{ 'map-pick-mode': pickPointMode }"></div>

    <div ref="layerSwitcherRoot" class="map-layer-switcher">
      <button
        type="button"
        class="btn btn-secondary map-layer-switcher__toggle"
        :aria-expanded="panelOpen"
        aria-haspopup="dialog"
        @click="panelOpen = !panelOpen"
      >
        Слои
        <span class="map-layer-switcher__caret" aria-hidden="true">▾</span>
      </button>

      <div v-if="panelOpen" class="map-layer-switcher__panel panel" role="dialog" aria-label="Слои карты">
        <p class="map-layer-switcher__title">Подложка</p>
        <div class="map-layer-switcher__base-options" role="radiogroup" aria-label="Базовый слой">
          <label
            v-for="layer in baseLayerOptions"
            :key="layer.id"
            class="map-layer-switcher__radio"
          >
            <input
              v-model="baseLayer"
              type="radio"
              name="map-base-layer"
              :value="layer.id"
              @change="onBaseLayerChange"
            />
            <span>{{ layer.label }}</span>
          </label>
        </div>

        <p class="map-layer-switcher__title">Оверлеи</p>
        <label class="map-layer-switcher__checkbox">
          <input v-model="showHillshade" type="checkbox" @change="onHillshadeChange" />
          <span>Hillshade</span>
        </label>
        <label class="map-layer-switcher__checkbox">
          <input v-model="enableTerrain3d" type="checkbox" @change="onTerrain3dChange" />
          <span>3D рельеф</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import maplibregl from 'maplibre-gl'
import { useAuthStore } from '../stores/auth'
import { defaultMapCenter } from '../utils/mapDefaults'
import {
  applyHybridTransform,
  BASE_LAYERS,
  buildReliefStyle,
  buildSatelliteStyle,
  DEM_TILEJSON,
  HILLSHADE_LAYER,
  TERRAIN_SOURCE,
} from '../utils/mapLayerConfig'
import { trackMapColor } from '../utils/trackMapColors'

const props = defineProps({
  tracks: {
    type: Array,
    default: () => [],
  },
  selectedTrackId: {
    type: [Number, String, null],
    default: null,
  },
  comments: {
    type: Array,
    default: () => [],
  },
  trackPhotos: {
    type: Array,
    default: () => [],
  },
  showPhotoMarkers: {
    type: Boolean,
    default: false,
  },
  pickPointMode: {
    type: Boolean,
    default: false,
  },
  pickedPoint: {
    type: Object,
    default: null,
  },
  showEndpoints: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['pick-point', 'photo-marker-click', 'track-photo-click'])

const auth = useAuthStore()

const mapRoot = ref(null)
const layerSwitcherRoot = ref(null)
const baseLayer = ref('scheme')
const showHillshade = ref(false)
const enableTerrain3d = ref(false)
const panelOpen = ref(false)

const baseLayerOptions = BASE_LAYERS

let map = null
let mapLoaded = false
let mapClickHandler = null
let layerEventsBound = false
let styleChangeInProgress = false
const endpointMarkers = []

const TRACKS_SOURCE = 'tracks'
const TRACKS_LAYER = 'tracks-layer'
const PHOTO_SOURCE = 'photo-markers'
const PHOTO_LAYER = 'photo-markers-layer'
const DESCRIPTION_PHOTO_SOURCE = 'description-photo-markers'
const DESCRIPTION_PHOTO_LAYER = 'description-photo-markers-layer'
const PICKED_SOURCE = 'picked-point'
const PICKED_LAYER = 'picked-point-layer'

const emptyCollection = () => ({ type: 'FeatureCollection', features: [] })

function toLngLat([lat, lng]) {
  return [lng, lat]
}

function toBounds(coordinates) {
  const lngs = coordinates.map(([, lng]) => lng)
  const lats = coordinates.map(([lat]) => lat)
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ]
}

function trackColor(track, index) {
  return track.color || trackMapColor(index)
}

function getSource(sourceId) {
  if (!map?.getSource(sourceId)) return null
  return map.getSource(sourceId)
}

function clearEndpointMarkers() {
  endpointMarkers.forEach((marker) => marker.remove())
  endpointMarkers.length = 0
}

function createEndpointElement(label, color, pointType) {
  const element = document.createElement('div')
  element.className = `track-endpoint track-endpoint--${pointType}`
  element.textContent = label
  element.style.setProperty('--endpoint-color', color)
  return element
}

function customLayerInsertBeforeId() {
  const styleLayers = map.getStyle()?.layers ?? []
  const firstSymbol = styleLayers.find((layer) => layer.type === 'symbol')
  return firstSymbol?.id
}

function hillshadeInsertBeforeId() {
  if (map.getLayer(TRACKS_LAYER)) return TRACKS_LAYER
  return customLayerInsertBeforeId()
}

function ensureSource(sourceId, spec) {
  if (!map || getSource(sourceId)) return
  map.addSource(sourceId, spec)
}

function ensureLayer(layerId, spec, beforeId) {
  if (!map || map.getLayer(layerId)) return
  map.addLayer(spec, beforeId)
}

function ensureTerrainSource() {
  if (!map || getSource(TERRAIN_SOURCE)) return

  map.addSource(TERRAIN_SOURCE, {
    type: 'raster-dem',
    url: DEM_TILEJSON,
    tileSize: 256,
  })
}

function ensureHillshadeLayer() {
  if (!map) return

  ensureTerrainSource()

  if (map.getLayer(HILLSHADE_LAYER)) {
    updateHillshadeVisibility()
    return
  }

  map.addLayer(
    {
      id: HILLSHADE_LAYER,
      type: 'hillshade',
      source: TERRAIN_SOURCE,
      layout: { visibility: showHillshade.value ? 'visible' : 'none' },
      paint: {
        'hillshade-shadow-color': '#473B24',
        'hillshade-exaggeration': 0.45,
      },
    },
    hillshadeInsertBeforeId()
  )
}

function updateHillshadeVisibility() {
  if (!map?.getLayer(HILLSHADE_LAYER)) return

  map.setLayoutProperty(
    HILLSHADE_LAYER,
    'visibility',
    showHillshade.value ? 'visible' : 'none'
  )
}

function updateTerrain3d() {
  if (!map || !mapLoaded) return

  if (enableTerrain3d.value) {
    ensureTerrainSource()
    map.setTerrain({ source: TERRAIN_SOURCE, exaggeration: 1.2 })
    map.setSky({})
    return
  }

  map.setTerrain(null)
}

function onPhotoLayerClick(event) {
  const commentId = event.features?.[0]?.properties?.commentId
  if (commentId == null) return

  const comment = photoComments().find((item) => item.id == commentId)
  if (comment) emit('photo-marker-click', comment)
}

function onDescriptionPhotoLayerClick(event) {
  const photoIndex = event.features?.[0]?.properties?.photoIndex
  if (photoIndex == null) return

  emit('track-photo-click', { index: Number(photoIndex) })
}

function onPhotoLayerMouseEnter() {
  map.getCanvas().style.cursor = 'pointer'
}

function onDescriptionPhotoLayerMouseEnter() {
  map.getCanvas().style.cursor = 'pointer'
}

function onPhotoLayerMouseLeave() {
  if (!props.pickPointMode) {
    map.getCanvas().style.cursor = ''
  }
}

function onDescriptionPhotoLayerMouseLeave() {
  if (!props.pickPointMode) {
    map.getCanvas().style.cursor = ''
  }
}

function bindLayerEvents() {
  if (!map || layerEventsBound) return

  map.on('click', PHOTO_LAYER, onPhotoLayerClick)
  map.on('click', DESCRIPTION_PHOTO_LAYER, onDescriptionPhotoLayerClick)
  map.on('mouseenter', PHOTO_LAYER, onPhotoLayerMouseEnter)
  map.on('mouseenter', DESCRIPTION_PHOTO_LAYER, onDescriptionPhotoLayerMouseEnter)
  map.on('mouseleave', PHOTO_LAYER, onPhotoLayerMouseLeave)
  map.on('mouseleave', DESCRIPTION_PHOTO_LAYER, onDescriptionPhotoLayerMouseLeave)
  layerEventsBound = true
}

function unbindLayerEvents() {
  if (!map || !layerEventsBound) return

  map.off('click', PHOTO_LAYER, onPhotoLayerClick)
  map.off('click', DESCRIPTION_PHOTO_LAYER, onDescriptionPhotoLayerClick)
  map.off('mouseenter', PHOTO_LAYER, onPhotoLayerMouseEnter)
  map.off('mouseenter', DESCRIPTION_PHOTO_LAYER, onDescriptionPhotoLayerMouseEnter)
  map.off('mouseleave', PHOTO_LAYER, onPhotoLayerMouseLeave)
  map.off('mouseleave', DESCRIPTION_PHOTO_LAYER, onDescriptionPhotoLayerMouseLeave)
  layerEventsBound = false
}

function ensureCustomDataLayers() {
  if (!map) return

  const beforeSymbol = customLayerInsertBeforeId()

  ensureSource(TRACKS_SOURCE, { type: 'geojson', data: emptyCollection() })
  ensureLayer(
    TRACKS_LAYER,
    {
      id: TRACKS_LAYER,
      type: 'line',
      source: TRACKS_SOURCE,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 4,
      },
    },
    beforeSymbol
  )

  ensureSource(PHOTO_SOURCE, { type: 'geojson', data: emptyCollection() })
  ensureLayer(PHOTO_LAYER, {
    id: PHOTO_LAYER,
    type: 'circle',
    source: PHOTO_SOURCE,
    paint: {
      'circle-radius': 8,
      'circle-color': '#2563eb',
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  })

  ensureSource(DESCRIPTION_PHOTO_SOURCE, { type: 'geojson', data: emptyCollection() })
  ensureLayer(DESCRIPTION_PHOTO_LAYER, {
    id: DESCRIPTION_PHOTO_LAYER,
    type: 'circle',
    source: DESCRIPTION_PHOTO_SOURCE,
    paint: {
      'circle-radius': 8,
      'circle-color': '#f59e0b',
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  })

  ensureSource(PICKED_SOURCE, { type: 'geojson', data: emptyCollection() })
  ensureLayer(PICKED_LAYER, {
    id: PICKED_LAYER,
    type: 'circle',
    source: PICKED_SOURCE,
    paint: {
      'circle-radius': 10,
      'circle-color': '#dc2626',
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  })
}

function syncMapState({ fitToTracks = false } = {}) {
  if (!map || !mapLoaded) return

  ensureCustomDataLayers()
  ensureHillshadeLayer()
  updateHillshadeVisibility()
  updateTerrain3d()
  bindLayerEvents()
  renderTracks()
  renderEndpoints()
  renderPhotoMarkers()
  renderDescriptionPhotoMarkers()
  renderPickedPoint()
  updateMapClickHandler()

  if (fitToTracks) {
    scheduleFitToTracks()
  }
}

function getBaseLayerConfig(layerId) {
  return BASE_LAYERS.find((layer) => layer.id === layerId) ?? BASE_LAYERS[0]
}

function applyBaseLayer(layerId) {
  if (!map) return

  const config = getBaseLayerConfig(layerId)
  styleChangeInProgress = true
  unbindLayerEvents()

  const onStyleReady = () => {
    map.off('style.load', onStyleReady)
    styleChangeInProgress = false
    mapLoaded = true
    syncMapState()
  }

  map.once('style.load', onStyleReady)

  if (config.kind === 'satellite') {
    map.setStyle(buildSatelliteStyle(), { diff: false })
    return
  }

  if (config.kind === 'hybrid') {
    map.setStyle(config.style, {
      diff: false,
      transformStyle: (_previousStyle, nextStyle) => applyHybridTransform(nextStyle),
    })
    return
  }

  if (config.kind === 'relief') {
    map.setStyle(buildReliefStyle(), { diff: false })
    return
  }

  map.setStyle(config.style, { diff: false })
}

function onBaseLayerChange() {
  if (!mapLoaded) return
  applyBaseLayer(baseLayer.value)
}

function onHillshadeChange() {
  if (!mapLoaded) return

  if (showHillshade.value) {
    ensureHillshadeLayer()
  }
  updateHillshadeVisibility()
}

function onTerrain3dChange() {
  if (!mapLoaded) return
  updateTerrain3d()
}

function handleDocumentClick(event) {
  if (!layerSwitcherRoot.value?.contains(event.target)) {
    panelOpen.value = false
  }
}

function ensureMap() {
  if (map) return

  map = new maplibregl.Map({
    container: mapRoot.value,
    style: getBaseLayerConfig(baseLayer.value).style,
    center: toLngLat(defaultMapCenter(auth.user)),
    zoom: 10,
    maxPitch: 85,
  })

  map.on('load', () => {
    mapLoaded = true
    syncMapState({ fitToTracks: true })
  })
}

function allBoundsPoints() {
  const boundsPoints = []
  props.tracks.forEach((track) => {
    if (track.coordinates?.length) {
      boundsPoints.push(...track.coordinates)
    }
  })
  return boundsPoints
}

function fitMapToTracks() {
  if (!map || !mapLoaded) return

  const boundsPoints = allBoundsPoints()

  if (props.selectedTrackId) {
    const selected = props.tracks.find((track) => track.id === props.selectedTrackId)
    if (selected?.coordinates?.length) {
      map.fitBounds(toBounds(selected.coordinates), { padding: 40 })
      return
    }
  }

  if (boundsPoints.length) {
    map.fitBounds(toBounds(boundsPoints), { padding: 40 })
    return
  }

  if (props.pickedPoint) {
    map.flyTo({
      center: toLngLat([props.pickedPoint.lat, props.pickedPoint.lng]),
      zoom: 14,
    })
  }
}

function scheduleFitToTracks() {
  if (!map || !mapLoaded || styleChangeInProgress) return

  map.resize()
  fitMapToTracks()

  nextTick(() => {
    requestAnimationFrame(() => {
      if (!map) return
      map.resize()
      fitMapToTracks()
    })
  })
}

function renderTracks() {
  if (!map || !mapLoaded) return

  const source = getSource(TRACKS_SOURCE)
  if (!source) return

  const features = props.tracks.flatMap((track, index) => {
    if (!track.coordinates?.length) return []

    return {
      type: 'Feature',
      properties: { color: trackColor(track, index) },
      geometry: {
        type: 'LineString',
        coordinates: track.coordinates.map(toLngLat),
      },
    }
  })

  source.setData({ type: 'FeatureCollection', features })
}

function renderEndpoints() {
  if (!map || !mapLoaded) return

  clearEndpointMarkers()

  if (!props.showEndpoints) return

  props.tracks.forEach((track, index) => {
    if (!track.coordinates?.length) return

    const color = trackColor(track, index)
    const start = track.coordinates[0]
    const finish = track.coordinates[track.coordinates.length - 1]

    endpointMarkers.push(
      new maplibregl.Marker({
        element: createEndpointElement('С', color, 'start'),
        anchor: 'center',
      })
        .setLngLat(toLngLat(start))
        .addTo(map)
    )

    if (track.coordinates.length > 1) {
      endpointMarkers.push(
        new maplibregl.Marker({
          element: createEndpointElement('Ф', color, 'finish'),
          anchor: 'center',
        })
          .setLngLat(toLngLat(finish))
          .addTo(map)
      )
    }
  })
}

function photoComments() {
  return props.comments.filter(
    (comment) => comment.lat != null && comment.lng != null && comment.photos?.length
  )
}

function renderPhotoMarkers() {
  if (!map || !mapLoaded) return

  const source = getSource(PHOTO_SOURCE)
  if (!source) return

  if (!props.showPhotoMarkers) {
    source.setData(emptyCollection())
    return
  }

  const features = photoComments().map((comment) => ({
    type: 'Feature',
    properties: { commentId: comment.id },
    geometry: {
      type: 'Point',
      coordinates: toLngLat([comment.lat, comment.lng]),
    },
  }))

  source.setData({ type: 'FeatureCollection', features })
}

function renderDescriptionPhotoMarkers() {
  if (!map || !mapLoaded) return

  const source = getSource(DESCRIPTION_PHOTO_SOURCE)
  if (!source) return

  if (!props.showPhotoMarkers) {
    source.setData(emptyCollection())
    return
  }

  const features = props.trackPhotos.map((photo) => ({
    type: 'Feature',
    properties: { photoIndex: photo.index },
    geometry: {
      type: 'Point',
      coordinates: toLngLat([photo.lat, photo.lng]),
    },
  }))

  source.setData({ type: 'FeatureCollection', features })
}

function renderPickedPoint() {
  if (!map || !mapLoaded) return

  const source = getSource(PICKED_SOURCE)
  if (!source) return

  if (!props.pickedPoint) {
    source.setData(emptyCollection())
    return
  }

  source.setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: toLngLat([props.pickedPoint.lat, props.pickedPoint.lng]),
        },
      },
    ],
  })
}

function updateMapClickHandler() {
  if (!map || !mapLoaded) return

  if (mapClickHandler) {
    map.off('click', mapClickHandler)
    mapClickHandler = null
  }

  if (!props.pickPointMode) return

  mapClickHandler = (event) => {
    if (event.features?.length) return

    emit('pick-point', { lat: event.lngLat.lat, lng: event.lngLat.lng })
  }
  map.on('click', mapClickHandler)
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  ensureMap()
})

watch(
  () => [props.tracks, props.selectedTrackId, props.showEndpoints],
  () => {
    renderTracks()
    renderEndpoints()
    scheduleFitToTracks()
  },
  { deep: true }
)

watch(
  () => [props.comments, props.trackPhotos, props.showPhotoMarkers, props.pickPointMode, props.pickedPoint],
  () => {
    renderPhotoMarkers()
    renderDescriptionPhotoMarkers()
    renderPickedPoint()
    updateMapClickHandler()
    scheduleFitToTracks()
  },
  { deep: true }
)

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  if (map && mapClickHandler) {
    map.off('click', mapClickHandler)
  }
  unbindLayerEvents()
  clearEndpointMarkers()
  map?.remove()
  map = null
  mapLoaded = false
  mapClickHandler = null
})
</script>

<style scoped>
.track-map {
  position: relative;
  width: 100%;
  height: 100%;
}

.map-container {
  width: 100%;
  height: 100%;
}

.map-pick-mode {
  cursor: crosshair;
}

.map-layer-switcher {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1000;
}

.map-layer-switcher__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}

.map-layer-switcher__caret {
  font-size: 0.75rem;
  line-height: 1;
}

.map-layer-switcher__panel {
  margin-top: 0.5rem;
  min-width: 12rem;
  padding: 0.75rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
}

.map-layer-switcher__title {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted, #64748b);
}

.map-layer-switcher__title + .map-layer-switcher__title {
  margin-top: 0.75rem;
}

.map-layer-switcher__base-options {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.map-layer-switcher__radio,
.map-layer-switcher__checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
}

.map-layer-switcher__radio input,
.map-layer-switcher__checkbox input {
  margin: 0;
}

@media (max-width: 767px) {
  .map-layer-switcher {
    top: 0.75rem;
    left: 0.75rem;
  }

  .map-layer-switcher__panel {
    max-width: calc(100vw - 2rem);
  }
}
</style>

<style>
.track-endpoint {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  background: var(--endpoint-color);
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.35);
  pointer-events: none;
  user-select: none;
}

.track-endpoint--finish {
  background: #fff;
  color: var(--endpoint-color);
  border-color: var(--endpoint-color);
}
</style>
